/**
 * Utilities for Purchase Orders
 */

/**
 * Calculates Indian Financial Year in format '26-27' (April to March)
 * @param {string|Date} dateInput
 * @returns {string} e.g. '26-27'
 */
export function calculateFinancialYear(dateInput) {
  const date = dateInput ? new Date(dateInput) : new Date()
  const validDate = isNaN(date.getTime()) ? new Date() : date
  const year = validDate.getFullYear()
  const month = validDate.getMonth() // 0 = Jan, 3 = Apr

  const startYear = month >= 3 ? year : year - 1
  const endYear = startYear + 1

  return `${String(startYear).slice(-2)}-${String(endYear).slice(-2)}`
}

/**
 * Generates the next Purchase Order number in the format 'SDPL/PO/26-27/2145'
 * Starting at 2145, and incrementing for each subsequent order.
 *
 * @param {Array} existingOrders
 * @param {string|Date} dateInput
 * @returns {string}
 */
export function generateNextPurchaseOrderNumber(
  existingOrders = [],
  dateInput,
) {
  const fy = calculateFinancialYear(dateInput)
  const prefix = `SDPL/PO/${fy}/`
  let maxSeq = 2144

  if (Array.isArray(existingOrders)) {
    existingOrders.forEach((order) => {
      const orderNo = order?.purchaseOrderNumber?.trim() || ''
      if (orderNo.startsWith(prefix)) {
        const suffix = orderNo.slice(prefix.length)
        const seq = parseInt(suffix, 10)
        if (!isNaN(seq) && seq > maxSeq) {
          maxSeq = seq
        }
      }
    })
  }

  return `${prefix}${maxSeq + 1}`
}

/**
 * Formats a date to readable format (e.g. '15 Sep 2026')
 * @param {string|Date} dateInput
 * @returns {string}
 */
export function formatDisplayDate(dateInput) {
  if (!dateInput) return '—'
  const date = new Date(dateInput)
  if (isNaN(date.getTime())) return String(dateInput)

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Formats a number to Indian Rupee currency
 * @param {number|string} amount
 * @returns {string}
 */
export function formatCurrency(amount) {
  const num = Number(amount) || 0
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(num)
}
